import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { Amenity } from "../types/Amenity";
import type { PaginatedResponse } from "../types/Pagination";
import type { Room, RoomSearchCriteria, RoomSuggestion } from "../types/Room";
import type { IRoomAPI } from "./roomApi";

class RemoteRoomAPI implements IRoomAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async fetchRoomById(id: string): Promise<Room | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/rooms/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      const room: Room = json.room;
      return room;
    } catch (error) {
      log("remoteRoomApi", "Failed to fetch room", error);
      throw error;
    }
  }

  async fetchSerializedRooms(): Promise<PaginatedResponse<Room>> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/rooms`);
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result: PaginatedResponse<Room> = await response.json();
      return result;
    } catch (error) {
      log("remoteRoomApi", "Failed to fetch rooms", error);
      throw error;
    }
  }

  async fetchAllRooms(): Promise<Room[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/rooms`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      // Ember returns `{ rooms: Room[] }` from GET /api/rooms.
      const rooms: Array<Room> = Array.isArray(json) ? json : json.rooms ?? [];
      return rooms;
    } catch (error) {
      log("remoteRoomApi", "Failed to fetch all rooms", error);
      throw error;
    }
  }

  async fetchAllRoomsForLocation(locationId: string): Promise<Room[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/rooms/location/${locationId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      const rooms: Array<Room> = json;
      return rooms;
    } catch (error) {
      log("remoteRoomApi", "Failed to fetch rooms for location", error);
      throw error;
    }
  }

  async fetchAvailableRoomsForLocation(locationId: string): Promise<Room[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/locations/${locationId}/rooms?available=true`);
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const rooms: Array<Room> = await response.json();
      return rooms;
    } catch (error) {
      log("remoteRoomApi", "Failed to fetch available rooms for location", error);
      throw error;
    }
  }

  async fetchAmenitiesForRoom(roomId: string): Promise<Array<Amenity>> {
    try {
      const room = await this.fetchRoomById(roomId);
      if (!room) return [];
      return Array.isArray(room.amenities) ? room.amenities : [];
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch amenities for location", error);
      throw error;
    }
  }

  async fetchGalleryForRoom(roomId: string): Promise<Array<string>> {
    try {
      const room = await this.fetchRoomById(roomId);
      if (!room) return [];
      return Array.isArray(room.gallery) ? room.gallery : [];
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch gallery for location", error);
      throw error;
    }
  }

  async findRooms(criteria: RoomSearchCriteria): Promise<RoomSuggestion[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/search/rooms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(criteria),
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const rooms: Array<RoomSuggestion> = await response.json();
      return rooms;
    } catch (error) {
      log("remoteRoomApi", "Failed to search for rooms", error);
      throw error;
    }
  }
}

export default RemoteRoomAPI;
